import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

type PlatformVolumeBreakdownProgram = {
  programId: string
  name: string
  displayName: string
  color: string
  highPriorityEngagementCount: number
  highPriorityWithinTargetCount: number
  highPriorityMissedTargetCount: number
  highPriorityWithinTargetPercentage: number
  engagementCount: number
  withinTargetCount: number
  missedTargetCount: number
  withinTargetPercentage: number
}

type PlatformVolumeResponse = {
  engagementCount: {
    total: number
    dailyAvg: number
    trend: number
  }
  withinTarget: {
    current: number
    trend: number
  }
  highPriority: {
    current: number
    total: number
    trend: number
  }
  summary: {
    programId: string
    name: string
    displayName: string
    color: string
    missedTargetPercentage: number
    missedTargetCount: number
    withinTargetCount: number
    orgName: string
    orgProfileId: string
    logoUrl: string | null
  }[]
  breakdown: {
    [date: string]: {
      engagementCount: number
      highPriorityEngagementCount: number
      programs: PlatformVolumeBreakdownProgram[]
    }
  }
  volumeByProgram: {
    programId: string
    orgProfileId: string
    dailyAvg: number
    total: number
    trend: number
    withinTarget: number
    withinTargetTrend: number | null
    highPriorityEngagementCount: number
    highPriorityTrend: number | null
  }[]
}

type PlatformVolumeRequest = {
  start_time: string
  end_time: string
  granularity: 'daily' | 'monthly' | 'weekly'
  scope: 'live'
}

const PLATFORM_VOLUME_V3_KEY = 'platform-volume-v3'

const request = async ({
  granularity,
  scope,
  ...params
}: PlatformVolumeRequest): Promise<PlatformVolumeResponse> => {
  const { data }: { data: PlatformVolumeResponse } = await apiClient.get(`/int/v3/platform/volume`, {
    params: {
      ...params,
      granularity,
      scope,
    },
  })

  return data
}

export function useGetPlatformVolumeV3(params: Partial<PlatformVolumeRequest>) {
  return useQuery<PlatformVolumeResponse>({
    queryKey: [PLATFORM_VOLUME_V3_KEY, params.scope, params],
    queryFn: () => request(params as PlatformVolumeRequest),
    enabled: Boolean(params.start_time && params.end_time && params.granularity && params.scope),
  })
}
